import React, { FC, useMemo, useCallback, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import TimePeriodDropdown, {
  BEACON_PRO_TIME_PERIODS
} from 'src/components/BeaconV2Components/common/TimePeriodDropdown';
import ComparisonPeriodDropdown from 'src/components/BeaconV2Components/common/ComparisonPeriodDropdown';
import {
  BEACON_SUBTABS,
  BEACON_TABS,
  BEACON_CONTENT_GEN_TABS,
  useBeaconRoutes
} from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { RetailerSkuCreditBalanceHeader } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/RetailerSkuCreditBalanceHeader';
import { withRouter } from 'react-router-dom';
import {
  ForecastPeriodDropdown,
  ForecastPlanTypeDropdown,
  ForecastTypeDropdown,
  ForecastComparisonPeriodDropdown
} from '../BeaconV2Components/ExperimentalLayout/Forecasts/dropdowns';
import { getAppName, getQueryParamValue, shouldShowNewAtlas } from 'src/utils/app';
import { AppFilterButton } from 'src/components/BeaconV2Components/common/AppFilterButton/AppFilterButton';
import { useAppSelector, useLocation, useHistory } from 'src/utils/Hooks';
import { TimePeriod } from 'src/types/store/storeTypes';
import { TopicsSubtabHeaderButtons } from 'src/components/BeaconV2Components/common/SubtabHeaderButtonsGroups/TopicsSubtabHeaderButtons';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import EditBudgetButton from 'src/components/BeaconV2Components/common/EditBudgetButton';
import { ATLAS_SUBTABS, useAtlasV2Routes } from 'src/components/AtlasV2Components/AtlasV2Sidebar/useAtlasV2Routes';
import { AppName } from 'sl-api-connector';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import AddRulesButton from 'src/components/BeaconV2Components/common/AddRulesButton';
import { CreateContentButton } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/CreateContentButton';
import colors from 'src/utils/colors';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import {
  useMicroTrendFilterModalStore,
  useMicroTrendsFilterStore,
  useMicroTrendSaveAsSegmentStore
} from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendStore';
import MicroTrendTimePeriodDropdown from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendTimePeriodDropdown';
import { AddProjectButton } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Project/AddProjectButton';
import { useEntityMetadataEnhancer } from 'src/components/AtlasV2Components/Hooks/useEntityMetadataEnhancer';
import Loading from 'src/components/common/Loading';
import ReviewProgressHeader from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/ReviewProgressHeader';
import { useContentGenReviewStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores/contentGenReviewStore';
import LibraryProductHeaderCard from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/LibraryProductHeaderCard';
import { useProfileSummaryStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { useGetSingleProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import { mapToAddFormData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/dtoMappers';
import ArchiveProfileButton from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/ArchiveProfileButton';
import AddProfileButton from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/AddProfileButton';
import { onContentGenerationMode } from 'src/store/modules/user/selectors';
import { useBeaconSubtabValidationRedirect } from 'src/components/BeaconV2Components/hooks/useBeaconSubtabValidationRedirect';

const useStyles = makeStyles({
  commonHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  subtabTitleText: {
    fontFamily: 'Roboto',
    fontSize: '26px',
    fontWeight: 'bold',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: '0.19px',
    textAlign: 'left',
    color: colors.darkBlue,
    '-webkit-font-smoothing': 'antialiased'
  },
  buttonGroup: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center'
  }
});

interface SubtabHeaderProps {
  hideButtons?: boolean;
  location: any;
}

const SubtabHeader: FC<SubtabHeaderProps> = ({ hideButtons }) => {
  const classes = useStyles();
  const currentTab = getQueryParamValue('tab');
  const { currentRoute: currentBeaconRoute, currentSubtab: currentBeaconSubtab } = useBeaconRoutes();
  const { currentRoute: currentAtlasRoute, currentSubtab: currentAtlasSubtab } = useAtlasV2Routes();
  const appName = getAppName();
  const { isProductEntity } = useProductEntity();

  // Validate and redirect if current tab/subtab combination is invalid (e.g., after retailer switch)
  const { isRedirecting } = useBeaconSubtabValidationRedirect();

  const location = useLocation();
  const { openModal } = useMicroTrendFilterModalStore();
  const selectedCategoryIds = useMicroTrendsFilterStore((state) => state.selectedCategoryIds);
  const selectedSubcategoryIds = useMicroTrendsFilterStore((state) => state.selectedSubcategoryIds);
  const history = useHistory();
  const { getEntityDisplayName } = useEntityMetadataEnhancer();
  const { hasContentGenItems } = useContentGenReviewStore();
  const {
    saveAsSegment,
    isLoading: isLoadingSaveAsSegment,
    reset: resetSaveAsSegment
  } = useMicroTrendSaveAsSegmentStore();
  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);
  const retailer = useAppSelector((state) => state.retailer);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);

  // Profile duplication context (used on PROFILES_SETTINGS page)
  const viewMode = useProfileSummaryStore((s) => s.viewMode);
  const editingProfileId = useProfileSummaryStore((s) => s.editingProfileId);
  const setDuplicateProfileData = useProfileSummaryStore((s) => s.setDuplicateProfileData);
  const setViewMode = useProfileSummaryStore((s) => s.setViewMode);
  const { profile } = useGetSingleProfile(viewMode === 'edit' ? editingProfileId : undefined);

  // Reset loading state when location changes or component unmounts
  useEffect(() => {
    return () => {
      resetSaveAsSegment();
    };
  }, [location.pathname, resetSaveAsSegment]);

  const { currentRoute, currentSubtab } = (() => {
    switch (appName) {
      case AppName.Beacon:
        return { currentRoute: currentBeaconRoute, currentSubtab: currentBeaconSubtab };
      case AppName.Atlas:
        return { currentRoute: currentAtlasRoute, currentSubtab: currentAtlasSubtab };
      default:
        return { currentRoute: currentBeaconRoute, currentSubtab: currentBeaconSubtab };
    }
  })();

  // Get the current sidebar tab label
  const { label } = currentRoute;

  const handleSaveAsSegment = useCallback(async () => {
    const url = await saveAsSegment({ mainEntity, retailer, mainTimePeriod, getEntityDisplayName });
    if (url) {
      history.push(url);
    }
  }, [saveAsSegment, mainEntity, retailer, mainTimePeriod, getEntityDisplayName, history]);

  const handleDuplicateProfileFromHeader = useCallback(() => {
    if (currentSubtab !== BEACON_SUBTABS.PROFILES_SETTINGS) return;
    if (viewMode !== 'edit' || !profile || !editingProfileId) return;

    // Map the existing profile data to the form format and update the name
    const profileWithVariant = { variant: 'edit' as const, ...profile };
    const duplicateData = mapToAddFormData(profileWithVariant);
    duplicateData.profileName = `${duplicateData.profileName} - Duplicate`;

    // Store the duplicate data and switch to add mode
    setDuplicateProfileData(duplicateData);
    setViewMode('add');
  }, [currentSubtab, viewMode, profile, editingProfileId, setDuplicateProfileData, setViewMode]);

  const getAllowedTimePeriod = useMemo(() => {
    if (currentSubtab === BEACON_SUBTABS.GROWTH_MATRIX) {
      return (timePeriod: TimePeriod) => {
        return (
          [
            BEACON_PRO_TIME_PERIODS.LAST_FOUR_WEEKS,
            BEACON_PRO_TIME_PERIODS.LAST_THIRTEEN_WEEKS,
            BEACON_PRO_TIME_PERIODS.LAST_TWENTY_SIX_WEEKS,
            BEACON_PRO_TIME_PERIODS.LAST_FIFTY_TWO_WEEKS,
            BEACON_PRO_TIME_PERIODS.YEAR_TO_DATE
          ].includes(timePeriod.id) && timePeriod.id !== BEACON_PRO_TIME_PERIODS.MTD
        );
      };
    } else {
      return (timePeriod: TimePeriod) => {
        return timePeriod.id !== BEACON_PRO_TIME_PERIODS.MTD;
      };
    }
  }, [currentSubtab]);

  const subtabButtonMap = useMemo(() => {
    const entityType = mainEntity.type;

    const createContentButton = <CreateContentButton key="createContentButton" />;

    let filterElements = [];

    if (mainEntity.type !== 'product') {
      filterElements = [<AppFilterButton key="appFilterButton" />];
    }

    if (onContentGenerationMode() && (currentTab === BEACON_TABS.CONTENT_V2 || currentTab === BEACON_TABS.CONTENT_V3)) {
      filterElements.push(createContentButton);
    }

    // Hide filter on Dispute pages, since none would be applicable
    if (currentSubtab === BEACON_SUBTABS.DISPUTES) {
      filterElements = [];
    }

    if (currentSubtab === ATLAS_SUBTABS.MICRO_TRENDS) {
      if (mainEntity.type === 'category') {
        const hasActiveFilters = selectedCategoryIds?.length > 0 || selectedSubcategoryIds?.length > 0;
        filterElements = [
          [
            <SlButton
              key="microtrendsFilterBtn"
              onClick={openModal}
              active={hasActiveFilters}
              buttonSx={{ width: '64px' }}
            >
              Filter
            </SlButton>
          ]
        ];
      } else {
        filterElements = [];
      }
    }

    if (entityType === 'microtrend') {
      filterElements = [
        <SlButton
          key="saveAsSegmentBtn"
          onClick={handleSaveAsSegment}
          variant="contained"
          buttonSx={{ width: '150px', letterSpacing: '0.08px', border: '0px' }}
          textSx={{ letterSpacing: '0.08px' }}
        >
          Save as Segment
        </SlButton>
      ];
    }

    function getMicroTrendDropdownElements(entityType, currentSubtab) {
      const isMicrotrend = entityType === 'microtrend';
      const isMicroTrendsSubtab = currentSubtab === ATLAS_SUBTABS.MICRO_TRENDS;

      if (isMicrotrend && isMicroTrendsSubtab) return [...filterElements];
      if (isMicrotrend) {
        return [
          <TimePeriodDropdown key="timePeriodDropdown" allowedTimePeriods={getAllowedTimePeriod} />,
          <ComparisonPeriodDropdown key="comparisonPeriodDropdown" />,
          ...filterElements
        ];
      }
      if (isMicroTrendsSubtab) {
        return [<MicroTrendTimePeriodDropdown key="microTrendTimePeriodDropdown" />, ...filterElements];
      }
      return [
        <TimePeriodDropdown key="timePeriodDropdown" allowedTimePeriods={getAllowedTimePeriod} />,
        <ComparisonPeriodDropdown key="comparisonPeriodDropdown" />,
        ...filterElements
      ];
    }

    return {
      scorecardV2: getMicroTrendDropdownElements(entityType, currentSubtab),
      traffic: shouldShowNewAtlas()
        ? [
            <TimePeriodDropdown allowedTimePeriods={getAllowedTimePeriod} key="timePeriodDropdown" />,
            <ComparisonPeriodDropdown key="comparisonPeriodDropdown" />,
            ...(isProductEntity ? [] : [<AppFilterButton key="appFilterButton" />])
          ]
        : [<TopicsSubtabHeaderButtons key="topicSubtabButtons" />],
      conversion: [
        <TimePeriodDropdown allowedTimePeriods={getAllowedTimePeriod} key="timePeriodDropdown" />,
        <ComparisonPeriodDropdown key="comparisonPeriodDropdown" />,
        ...(isProductEntity ? [] : [<AppFilterButton key="appFilterButton" />])
      ],
      forecasts: [
        <ForecastPeriodDropdown key="forecastPeriodDropdown" />,
        <Text variant="subtitle2" key="vsText">
          vs.
        </Text>,

        <ForecastComparisonPeriodDropdown key="forecastComparisonPeriodDropdown" />,

        // Adjusted forecast / unadjusted forecast dropdown
        currentSubtab !== BEACON_SUBTABS.FORECAST_ADJUSTMENTS &&
        currentSubtab !== BEACON_SUBTABS.FORECAST_RECOMMENDATIONS ? (
          <ForecastTypeDropdown key="forecastTypeDropdown" />
        ) : null,

        // Organic Traffic / Paid Traffic / Content Score etc. dropdown
        currentSubtab === BEACON_SUBTABS.FORECAST_ADJUSTMENTS ||
        currentSubtab === BEACON_SUBTABS.FORECAST_RECOMMENDATIONS ? (
          <ForecastPlanTypeDropdown key="forecastPlanTypeDropdown" />
        ) : null,

        ...filterElements
      ],
      advertising: (() => {
        switch (currentSubtab) {
          case BEACON_SUBTABS.BUDGET_ENTITY:
          case BEACON_SUBTABS.BUDGET_ALLOCATION:
          case BEACON_SUBTABS.BUDGET_CONFIRMATION:
          case BEACON_SUBTABS.RULES_DETAILS:
          case BEACON_SUBTABS.PORTFOLIO_SETTINGS:
          case BEACON_SUBTABS.PORTFOLIO_CAMPAIGNS:
          case BEACON_SUBTABS.PORTFOLIO_CONFIRMATION:
            return [];
          case BEACON_SUBTABS.INELIGIBLE:
            return [<EditBudgetButton key="editBudgetButton" />];
          case BEACON_SUBTABS.RULES_SUMMARY:
            return [<AddRulesButton key="addRulesButton" />];

          default:
            return [
              <TimePeriodDropdown key="timePeriodDropdown" />,
              <ComparisonPeriodDropdown key="comparisonPeriodDropdown" />,
              <EditBudgetButton key="editBudgetButton" />
            ];
        }
      })(),

      default: [
        <TimePeriodDropdown allowedTimePeriods={getAllowedTimePeriod} key="timePeriodDropdown" />,
        <ComparisonPeriodDropdown key="comparisonPeriodDropdown" />,
        ...filterElements
      ],
      content_v2: filterElements // Add the content_v2 tab mapping here
    };
  }, [
    mainEntity.type,
    currentTab,
    currentSubtab,
    getAllowedTimePeriod,
    isProductEntity,
    selectedCategoryIds,
    selectedSubcategoryIds,
    openModal,
    handleSaveAsSegment
  ]);

  // Early return if redirecting to a valid subtab (must be after all hooks)
  if (isRedirecting) return null;

  const buttonsToRender = subtabButtonMap[currentTab] || subtabButtonMap.default;

  if (BEACON_CONTENT_GEN_TABS.includes(currentTab as BEACON_TABS)) {
    // Only show RetailerSkuCreditBalanceHeader on single review (generation review subtab)
    const showCreditBalanceHeader = [BEACON_SUBTABS.GENERATION_REVIEW, BEACON_SUBTABS.GENERATE].includes(
      currentSubtab as BEACON_SUBTABS
    );
    const generationButtonMap = {
      [BEACON_SUBTABS.PROJECTS_REVIEW]: [hasContentGenItems() ? <ReviewProgressHeader key="reviewProgress" /> : null],
      [BEACON_SUBTABS.GENERATION_REVIEW]: [
        showCreditBalanceHeader ? <RetailerSkuCreditBalanceHeader key="creditHeader" /> : null
      ],
      [BEACON_SUBTABS.GENERATE]: [
        showCreditBalanceHeader ? <RetailerSkuCreditBalanceHeader key="creditHeader" /> : null
      ],
      [BEACON_SUBTABS.LIBRARY_REVIEW]: [<LibraryProductHeaderCard key="productHeaderCard" />],
      [BEACON_SUBTABS.LIBRARY]: [],
      [BEACON_SUBTABS.PROJECTS]: [<AddProjectButton key="addProject" />],
      [BEACON_SUBTABS.PROFILES_ACTIVE]: [<AddProfileButton key="addProfileBtn" />],
      [BEACON_SUBTABS.PROFILES_ARCHIVED]: [<AddProfileButton key="addProfileBtn" />],
      [BEACON_SUBTABS.PROFILES_SETTINGS]: [
        viewMode === 'edit' ? (
          <SlButton
            key="duplicateProfileHeaderBtn"
            onClick={handleDuplicateProfileFromHeader}
            buttonSx={{ border: '1px solid #E0E4E7' }}
          >
            Duplicate
          </SlButton>
        ) : null,
        <ArchiveProfileButton key="archiveProfile" />
      ]
    } as Record<string, Array<React.ReactNode>>;

    const buttons = generationButtonMap[currentSubtab]?.filter(Boolean) || [];
    let overrideLabel = label;

    if (currentTab === BEACON_TABS.PROJECTS && currentSubtab !== BEACON_SUBTABS.PROJECTS) {
      overrideLabel = 'Project Builder';
    } else if (currentTab === BEACON_TABS.PROFILES && currentSubtab.includes('Settings')) {
      overrideLabel = 'Profile Builder';
    }

    return (
      <div
        className={classes.commonHeaderContainer}
        style={{
          position: 'relative'
        }}
      >
        <div className={classes.subtabTitleText}>{overrideLabel}</div>
        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            position: 'absolute',
            top: 0,
            right: 0
          }}
        >
          {buttons.map((btn, idx) => (
            <React.Fragment key={idx}>{btn}</React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  const shouldHideButtons = !!hideButtons;

  if (isLoadingSaveAsSegment) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          zIndex: 9999
        }}
      >
        <Loading style={{ position: 'fixed' }} className="spinner" />
      </div>
    );
  }

  return (
    <div className={classes.commonHeaderContainer}>
      <div className={classes.subtabTitleText}>{label}</div>
      {shouldHideButtons ? null : (
        <div className={classes.buttonGroup}>
          {buttonsToRender.map((button, index) => {
            return <React.Fragment key={index}>{button}</React.Fragment>;
          })}
        </div>
      )}
    </div>
  );
};

export default withRouter(SubtabHeader);
